import { useState } from 'react';
import type { StepStatsRow } from '@reelmachine/core';
import { useErrorSheet, useStepStats } from '../../api/learning';
import { formatPercent, formatUsd } from '../../lib/learning-format';
import { Segmented } from '../../ui/Segmented';
import { QueryState } from './QueryState';

const WINDOWS = [
  { value: '7', label: '7 days' },
  { value: '28', label: '28 days' },
  { value: '90', label: '90 days' },
] as const;
type Window = (typeof WINDOWS)[number]['value'];

/** Steps slower than this on average are marked. */
export const SLOW_MS = 60_000;

const seconds = (ms: number) => `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
const tokens = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

function StepsTable({ rows }: { rows: StepStatsRow[] }) {
  if (!rows.length) return <p className="muted">No calls in this window.</p>;
  return (
    <div className="md-table">
      <table className="arms steps-table" aria-label="Step stats">
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Calls</th>
            <th scope="col">Avg</th>
            <th scope="col">p90</th>
            <th scope="col">Tokens in/out</th>
            <th scope="col">Cost</th>
            <th scope="col">Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const slow = r.avgLatencyMs > SLOW_MS;
            return (
              <tr key={r.step} className={slow ? 'slow' : undefined}>
                <th scope="row">
                  {r.step}
                  {slow && <span className="visually-hidden"> (slow)</span>}
                </th>
                <td>{r.calls}</td>
                <td>{seconds(r.avgLatencyMs)}</td>
                <td>{seconds(r.p90LatencyMs)}</td>
                <td>
                  {tokens(r.avgInputTokens)}/{tokens(r.avgOutputTokens)}
                </td>
                <td>{formatUsd(r.costUsd)}</td>
                <td>{formatPercent(r.errorRate)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Ng's error sheet: which pipeline component fails most across unsatisfactory reels. */
export function ErrorsTab() {
  const [win, setWin] = useState<Window>('28');
  const days = Number(win);
  const sheet = useErrorSheet(days);
  const steps = useStepStats(days);
  return (
    <>
      <Segmented label="Time window" value={win} options={WINDOWS} onChange={setWin} />
      <p className="small muted">
        An error means the step did worse than an expert would have with the same input. A step that only failed because
        its input was bad counts as upstream, not as an error.
      </p>
      <QueryState query={sheet}>
        {(s) => (
          <section className="card" aria-label="Error sheet">
            <span className="label">
              {s.reels} unsatisfactory {s.reels === 1 ? 'reel' : 'reels'}
            </span>
            {s.rows.length === 0 ? (
              <p className="muted">No errors in this window.</p>
            ) : (
              <ol className="bars" aria-label="Errors by component">
                {s.rows.map((r) => (
                  <li key={r.component} aria-label={`${r.component}: ${r.errors} errors, ${r.labelled} confirmed`}>
                    <div className="row between nowrap">
                      <span className="bar-name">{r.component}</span>
                      <span className="nums">
                        {r.errors} of {s.reels} · {formatPercent(r.rate)}
                        {r.labelled > 0 && ` · ${r.labelled} confirmed`}
                      </span>
                    </div>
                    <div className="track" aria-hidden="true">
                      <i style={{ width: `${Math.round(r.rate * 100)}%` }} />
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}
      </QueryState>
      <section className="field" aria-label="Steps">
        <span className="label">Steps</span>
        <QueryState query={steps}>{(rows) => <StepsTable rows={rows} />}</QueryState>
        <p className="small muted">Steps that take longer than 60 s on average are marked.</p>
      </section>
    </>
  );
}
